import React, { useState, useEffect } from 'react';
import { 
  Tooltip,
  Box,
  TextField,
  IconButton
} from '@mui/material';
import {
  ArrowDropUp,
  ArrowDropDown,
  Save
} from '@mui/icons-material';
import './PlaybackSpeedDropdown.css';

interface PlaybackSpeedDropdownProps {
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  disabled?: boolean;
}

const PlaybackSpeedDropdown: React.FC<PlaybackSpeedDropdownProps> = ({
  playbackRate,
  onPlaybackRateChange,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Update inputValue when playbackRate prop changes (for external updates)
  useEffect(() => {
    if (!isEditing) {
      setInputValue(playbackRate.toString());
    }
  }, [playbackRate, isEditing]);

  const validateInput = (value: string): boolean => {
    if (!value.trim()) return false;
    
    const num = parseFloat(value);
    if (isNaN(num)) return false;
    
    // Check range
    if (num < 0 || num > 5) return false;
    
    // Check decimal places (max 2)
    const decimalPlaces = (value.split('.')[1] || '').length;
    if (decimalPlaces > 2) return false;
    
    return true;
  };

  const handleEditClick = () => {
    if (disabled) return;
    setIsEditing(true);
    setInputValue(playbackRate.toString());
  };

  const handleSave = () => {
    if (!validateInput(inputValue)) {
      // Reset to original value on invalid input
      setInputValue(playbackRate.toString());
      setIsEditing(false);
      return;
    }
    
    const newRate = parseFloat(inputValue);
    onPlaybackRateChange(newRate);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setInputValue(playbackRate.toString());
    setIsEditing(false);
  };

  const handleIncrement = () => {
    if (disabled) return;
    const newRate = Math.min(playbackRate + 0.5, 5.0);
    onPlaybackRateChange(newRate);
  };

  const handleDecrement = () => {
    if (disabled) return;
    const newRate = Math.max(playbackRate - 0.5, 0.0);
    onPlaybackRateChange(newRate);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  const formatRate = (rate: number): string => {
    return `${rate.toFixed(2)}x`;
  };

  return (
    <Tooltip title="Playback Speed">
      <Box
        className={`playback-speed-container ${disabled ? 'disabled' : ''}`}
      >
        {isEditing ? (
          <>
            <TextField
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              autoFocus
              variant="standard"
              className="playback-speed-textfield"
              InputProps={{
                disableUnderline: true,
              }}
            />
            <IconButton
              onClick={handleSave}
              disabled={disabled}
              size="small"
              aria-label="Save playback rate"
              className="playback-speed-icon-button"
            >
              <Save fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Box
              onClick={handleEditClick}
              className={`playback-speed-display ${disabled ? 'disabled' : ''}`}
            >
              {formatRate(playbackRate)}
            </Box>
            <Box className="playback-speed-arrows">
              <IconButton
                onClick={handleIncrement}
                disabled={disabled || playbackRate >= 5.0}
                size="small"
                aria-label="Increment playback rate"
                className="playback-speed-arrow-button"
              >
                <ArrowDropUp fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleDecrement}
                disabled={disabled || playbackRate <= 0.0}
                size="small"
                aria-label="Decrement playback rate"
                className="playback-speed-arrow-button"
              >
                <ArrowDropDown fontSize="small" />
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

export default PlaybackSpeedDropdown;
